import React, { useState, useMemo } from 'react';
import { CnRadioGroup, CnBaseTable, CnButton, CnDialog, CnForm, formilyCore, CnPagination } from '@sto/cn-ui';
import CustomCnTable from './CustomCnTable';
import styles from './index.module.scss';

const { createForm } = formilyCore;

/**
 * 维度切换表格组件
 * @param param0 
 * @returns 
 */
const SwitchCTable = ({ content }) => {
  if (!content) return null;
  const [type, setType] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const dataSource = useMemo(() => {
    if (typeof content === 'string') {
      const contentObj = JSON.parse(content);
      if (Array.isArray(contentObj)) {
        setType(contentObj[0]?.value || 0);
        return contentObj;
      }
      return [];
    } else if (Array.isArray(content)) {
      const firstItem = content[0] || {};
      setType(firstItem.value || 0);
      return content;
    }
    return [];
  }, [content]);
  
  /** 处理表格列 */
  const formatColumns = (columns) => {
    return columns.map(item => {
      const { name, code, detail_action, ...rest } = item;
      const headerObj = {
        width: 100,
        ...rest,
        title: name,
        code: `${code}`.replace(/\./g, ''), // 处理点号
      };
        // 根据配置，定制渲染列
      if (detail_action) {
        // headerObj.lock = 'right';
        headerObj.cell = (v, i, r) => {
          const { type } = detail_action;
          if (type === 'button') {
            if (detail_action['x-component'] === 'dialog') {
              const componentProps = detail_action['x-component-props'];
              const { title } = componentProps;
              return <CnButton 
                type="primary"
                text
                onClick={() => {
                  CnDialog.show({
                    title,
                    content: <div className={styles.dialogContent}>
                      <FeeForm content={v} />
                    </div>,
                    footer: false,
                    width: 800,
                  });
                }}
              >
                {title}
              </CnButton>;
            }
            return <CnButton type="primary" text>{v}</CnButton>;
          }
          if (typeof v === 'string') {
            return <div>{v}</div>;
          }
          return <div>{JSON.stringify(v)}</div>;
        };
      }
      return headerObj;
    });
  };
  
  /** 处理表格行 */
  const formatRows = (rows) => {
    return rows.map(item => {
      const temp = {};
      // 处理带小数点的key无法映射对应的值的问题
      Object.keys(item).forEach((key) => {
        const newKey = `${key}`.replace(/\./g, '');
        temp[newKey] = item[key];
      });
      return temp;
    });
  };

  /** 处理表格数据 */
  const tableData = useMemo(() => {
    if (Array.isArray(dataSource) && dataSource.length > 0 && type) {
      const index = dataSource.findIndex(item => item.value === type);
      const { detail } = dataSource[index];
      return {
        header: formatColumns(detail?.columns || []),
        rows: formatRows(detail?.dataSources || []),
      };
    }
    return {};
  }, [dataSource, type]);

  /** 分页数据 */
  const paginatedData = useMemo(() => {
    const { rows } = tableData;
    if (!rows || !Array.isArray(rows)) return [];
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return rows.slice(startIndex, endIndex);
  }, [tableData, currentPage, pageSize]);

  return (
    <div className={styles.switchCTableBox}>
      <div className={styles.radioGroupBox}>
        <CnRadioGroup 
          shape="tag" 
          size="large"
          dataSource={dataSource} 
          value={type} 
          onChange={(value) => {
            setType(value);
          }}
        />
      </div>
      <CnBaseTable
        isStickyHeader
        style={{ overflow: 'auto', paddingBottom: 0 }}
        size="small"
        columns={tableData.header}
        dataSource={paginatedData}
        isZebra
        zebraColor="#FAFBFC"
      />
      {tableData.rows && tableData.rows.length > pageSize && (
        <CnPagination 
          totalCount={tableData.rows.length} 
          showJump={false} 
          pageSize={pageSize}
          currentPage={currentPage}
          onChange={(value) => setCurrentPage(value)}
          style={{ marginTop: '6px' }}
        />
      )}
    </div>
  );
};

export default SwitchCTable;


/** 费率表单组件 */
export const FeeForm = ({ content }) => {
  const cForm = useMemo(
    () =>
      createForm({
        readOnly: true,
        effects: () => {},
      }),
    [],
  );

  /**
   * 递归聚合所有 extraInfo.params.initialValues 的值
   * @param obj 要遍历的对象
   * @returns 聚合后的 values 对象
   */
  const aggregateInitialValues = (obj: any): any => {
    let values = {};
    
    const traverse = (current: any) => {
      if (!current || typeof current !== 'object') return;
      
      // 检查当前对象是否有 extraInfo.params.initialValues
      if (current.extraInfo?.params?.initialValues) {
        values = { ...values, ...current.extraInfo.params.initialValues };
      }
      
      // 递归遍历所有属性
      Object.keys(current).forEach(key => {
        if (current[key] && typeof current[key] === 'object') {
          if (Array.isArray(current[key])) {
            // 如果是数组，遍历每个元素
            current[key].forEach(item => traverse(item));
          } else {
            // 如果是对象，递归遍历
            traverse(current[key]);
          }
        }
      });
    };
    
    traverse(obj);
    return values;
  };

  const schema = useMemo(() => {
    let contentObj = typeof content === 'string' ? JSON.parse(content) : content;
    const values = aggregateInitialValues(contentObj);
    cForm.setValues(values);
    return contentObj;
  }, [content]);
  

  return <div className={styles.feeFormContent}>
    <CnForm 
      form={cForm} 
      className={styles.cForm} 
      schema={schema} 
      formLayoutProps={
        {
          labelCol: {
            fixedSpan: 5,
          },
        }
      }
      components={{
        CnTable: CustomCnTable,
      }}
    />
  </div>;
};
